import { Card } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import {
  Control,
  Controller,
  FieldErrors,
  UseFormWatch,
  UseFormSetValue,
} from "react-hook-form";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectTrigger,
  SelectValue,
  SelectItem,
} from "@/components/ui/select";
import { toast } from "@/components/ui/use-toast";
import { useState, useEffect } from "react";
import {
  Plus,
  Minus,
  Trash2,
  Shield,
  AlertCircle,
  Info,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { FacilitySchedule } from "@/types/facility/schedule";

interface EventPricingAndRefundProps {
  control: Control<FacilitySchedule>;
  errors: FieldErrors<FacilitySchedule>;
  watch: UseFormWatch<FacilitySchedule>;
  setValue: UseFormSetValue<FacilitySchedule>;
  open: boolean;
}

const EventPricingAndRefund = ({
  control,
  errors,
  watch,
  setValue,
  open,
}: EventPricingAndRefundProps) => {

  const [refundsEnabled, setRefundsEnabled] = useState(false);

  // Watch arrays to sync with toggle states
  const refundPolicies = watch("refund_policies") || [];

  // Watch form values for reactive date calculations
  const scheduleStartDate = watch("general_info.start_date");
  const scheduleEndDate = watch("general_info.end_date");
  const scheduleId = watch("general_info.id");
  const formData = watch();

  // Initialize & sync toggle states based on current form data (including after resets)
  useEffect(() => {
    setRefundsEnabled((refundPolicies || []).length > 0);
  }, [refundPolicies]);

  return (
    <div className="space-y-6">
      {/* Refund policy section */}
      <Card className="p-6 shadow-sm border-l-4 border-l-red-500">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-red-100 rounded-lg">
            <Shield className="h-5 w-5 text-red-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              Refund Policy
            </h2>
            <p className="text-sm text-gray-600">
              Configure cancellation periods and refund percentages
            </p>
          </div>
        </div>
        <Separator className="mb-6" />

        <div className="mb-6 p-4 bg-gradient-to-r from-red-50 to-pink-50 rounded-lg border border-red-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <Shield className="h-4 w-4 text-red-600" />
              </div>
              <div>
                <h3 className="font-medium text-gray-900">
                  Event Refund Rules
                </h3>
                <p className="text-xs text-gray-600">
                  Define cancellation periods and refund amounts
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Enable</span>
              <Switch
                checked={refundsEnabled}
                onCheckedChange={(checked) => {
                  setRefundsEnabled(checked);
                  if (checked) {
                    setValue("refund_policies", [
                      { cancellation_period: "", refund_percentage: 0 },
                    ]);
                  } else {
                    setValue("refund_policies", []);
                  }
                }}
              />
            </div>
          </div>

          {!refundsEnabled && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <div className="flex items-center gap-2 text-red-700 text-sm">
                <Info className="h-4 w-4" />
                <span>
                  When disabled, no refunds will be available for this facility
                  schedule.
                </span>
              </div>
            </div>
          )}
        </div>

        {refundsEnabled && (
          <div className="space-y-4">
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
              <div className="flex items-center gap-2 text-blue-700 text-sm">
                <Info className="h-4 w-4" />
                <span>
                  Set up to 7 refund rules. Each rule defines a cancellation
                  period and the corresponding refund percentage.
                </span>
              </div>
            </div>

            <Controller
              control={control}
              name="refund_policies"
              rules={{
                validate: {
                  refundPoliciesValid: (policies) => {
                    if (!policies || policies.length === 0)
                      return "At least one refund policy is required";
                    if (policies.length > 7)
                      return "Maximum 7 refund policies allowed";

                    const periods = new Set();
                    for (let i = 0; i < policies.length; i++) {
                      if (
                        !policies[i].cancellation_period ||
                        policies[i].cancellation_period.trim() === ""
                      ) {
                        return `Rule ${i + 1}: Cancellation period is required`;
                      }
                      if (periods.has(policies[i].cancellation_period)) {
                        return `Rule ${i + 1}: Duplicate cancellation period`;
                      }
                      periods.add(policies[i].cancellation_period);
                      if (
                        policies[i].refund_percentage <= 0 ||
                        policies[i].refund_percentage > 100
                      ) {
                        return `Rule ${i + 1}: Percentage must be between 0-100`;
                      }
                    }
                    return true;
                  },
                },
              }}
              render={({ field, fieldState: { error } }) => (
                <div>
                  {error && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                      <div className="flex items-center gap-2 text-red-700 text-sm">
                        <AlertCircle className="h-4 w-4" />
                        <span>{error.message}</span>
                      </div>
                    </div>
                  )}

                  <div className="space-y-4">
                    {field.value?.map((rule, index) => (
                      <Card
                        key={index}
                        className="p-4 bg-white border-2 border-red-100 hover:border-red-200 transition-colors"
                      >
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <Badge
                              variant="outline"
                              className="text-xs text-red-700 border-red-300"
                            >
                              Rule {index + 1}
                            </Badge>
                          </div>
                          {(field.value?.length || 0) > 1 && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                const newRefunds = [...(field.value || [])];
                                newRefunds.splice(index, 1);
                                field.onChange(newRefunds);
                              }}
                              className="h-6 w-6 p-0 text-gray-400 hover:text-red-500"
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          )}
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="text-sm font-medium text-gray-700 mb-1 block">
                              Cancellation Period{" "}
                              <span className="text-red-500">*</span>
                            </label>
                            <Select
                              value={rule.cancellation_period || ""}
                              onValueChange={(value) => {
                                const isDuplicate =
                                  field.value?.some(
                                    (r, i) =>
                                      i !== index &&
                                      r.cancellation_period === value,
                                  ) || false;

                                if (isDuplicate) {
                                  toast({
                                    variant: "destructive",
                                    description:
                                      "This cancellation period is already selected. Please choose a different period.",
                                  });
                                  return;
                                }

                                const newRefunds = [...(field.value || [])];
                                newRefunds[index].cancellation_period = value;
                                field.onChange(newRefunds);
                              }}
                            >
                              <SelectTrigger
                                className={
                                  errors.refund_policies?.[index]
                                    ?.cancellation_period
                                    ? "border-red-500"
                                    : ""
                                }
                              >
                                <SelectValue placeholder="Select cancellation period" />
                              </SelectTrigger>
                              <SelectContent>
                                {[
                                  {
                                    value: "1h",
                                    label: "1 hour before the booking starts",
                                  },
                                  {
                                    value: "2h",
                                    label: "2 hours before the booking starts",
                                  },
                                  {
                                    value: "4h",
                                    label: "4 hours before the booking starts",
                                  },
                                  {
                                    value: "6h",
                                    label: "6 hours before the booking starts",
                                  },
                                  {
                                    value: "12h",
                                    label: "12 hours before the booking starts",
                                  },
                                  {
                                    value: "24h",
                                    label: "24 hours before the booking starts",
                                  },
                                  {
                                    value: "2d",
                                    label: "2 days before the booking starts",
                                  },
                                  {
                                    value: "3d",
                                    label: "3 days before the booking starts",
                                  },
                                ].map((period) => (
                                  <SelectItem
                                    key={period.value}
                                    value={period.label}
                                  >
                                    {period.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            {errors.refund_policies?.[index]
                              ?.cancellation_period && (
                                <div className="flex items-center gap-1 text-red-600 text-xs mt-1">
                                  <AlertCircle className="h-3 w-3" />
                                  {
                                    errors.refund_policies?.[index]
                                      ?.cancellation_period?.message
                                  }
                                </div>
                              )}
                          </div>

                          <div>
                            <label className="text-sm font-medium text-gray-700 mb-1 block">
                              Refund Percentage{" "}
                              <span className="text-red-500">*</span>
                            </label>
                            <div className="flex items-center gap-2">
                              <Input
                                type="number"
                                min="1"
                                max="100"
                                step="1"
                                value={rule.refund_percentage || ""}
                                className={
                                  errors.refund_policies?.[index]
                                    ?.refund_percentage
                                    ? "border-red-500"
                                    : ""
                                }
                                onChange={(e) => {
                                  const value = parseInt(e.target.value);
                                  const newRefunds = [...(field.value || [])];

                                  if (isNaN(value) || value <= 0) {
                                    toast({
                                      variant: "destructive",
                                      description:
                                        "Percentage must be between 0-100",
                                    });
                                    newRefunds[index].refund_percentage = 1;
                                  } else if (value > 100) {
                                    toast({
                                      variant: "destructive",
                                      description:
                                        "Percentage cannot exceed 100%",
                                    });
                                    newRefunds[index].refund_percentage = 100;
                                  } else {
                                    newRefunds[index].refund_percentage = value;
                                  }

                                  field.onChange(newRefunds);
                                }}
                                placeholder="0-100"
                              />
                              <span className="text-sm text-gray-500 min-w-[20px]">
                                %
                              </span>
                            </div>
                            {errors.refund_policies?.[index]
                              ?.refund_percentage && (
                                <div className="flex items-center gap-1 text-red-600 text-xs mt-1">
                                  <AlertCircle className="h-3 w-3" />
                                  {
                                    errors.refund_policies?.[index]
                                      ?.refund_percentage?.message
                                  }
                                </div>
                              )}
                          </div>
                        </div>
                      </Card>
                    ))}

                    <div className="flex items-center justify-between pt-4 border-t border-red-200">
                      <div className="flex items-center gap-3">
                        <Button
                          type="button"
                          variant="outline"
                          className="border-red-300 text-red-700 hover:bg-red-50 hover:border-red-400"
                          onClick={() => {
                            if ((field.value?.length || 0) < 7) {
                              field.onChange([
                                ...(field.value || []),
                                {
                                  cancellation_period: "",
                                  refund_percentage: 0,
                                },
                              ]);
                            } else {
                              toast({
                                variant: "destructive",
                                description:
                                  "Maximum 7 refund criteria are allowed.",
                              });
                            }
                          }}
                          disabled={(field.value?.length || 0) >= 7}
                        >
                          <Plus className="h-4 w-4 mr-2" />
                          Add Refund Rule
                        </Button>

                        {(field.value?.length || 0) > 1 && (
                          <Button
                            type="button"
                            variant="ghost"
                            className="text-gray-500 hover:text-red-500"
                            onClick={() => {
                              const newRefunds = [...(field.value || [])];
                              newRefunds.pop();
                              field.onChange(newRefunds);
                            }}
                          >
                            <Minus className="h-4 w-4 mr-2" />
                            Remove Last
                          </Button>
                        )}
                      </div>

                      <div className="text-xs text-gray-500">
                        {field.value?.length || 0}/7 rules
                      </div>
                    </div>

                    {(field.value?.length || 0) >= 7 && (
                      <div className="p-3 bg-amber-50 border border-amber-200 rounded-md">
                        <div className="flex items-center gap-2 text-amber-700 text-sm">
                          <Info className="h-4 w-4" />
                          <span>
                            You've reached the maximum of 7 refund rules.
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            />
          </div>
        )}
      </Card>
    </div>
  );
};

export default EventPricingAndRefund;
